import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Divider,
  alpha,
  Stack,
  Link,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  LocalShipping as VehicleIcon,
  Visibility as VisibilityIcon,
  Autorenew as ReplaceIcon,
} from "@mui/icons-material";
import {
  vehicleCreate,
  getVehicleDetails,
  updateVehicle,
  vehicleList,
} from "../../../Services/Services";

// ─── Constants ───────────────────────────────────────────────────
import { brandColor as brand } from "../../../theme";

const VEHICLE_NUMBER_REGEX =
  /^[A-Z]{2}[\s-]?[0-9]{1,2}[\s-]?[A-Z]{1,3}[\s-]?[0-9]{4}$/i;
const CHASSIS_NUMBER_REGEX = /^[A-Z0-9]{5,17}$/i;

// ─── Shared style objects ─────────────────────────────────────────

// Matching AddNewUser textfield style with background color + border radius
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    "& fieldset": {
      borderColor: "#e5e7eb",
    },
    "&:hover fieldset": {
      borderColor: "#c7d2fe",
    },
    "&.Mui-focused fieldset": {
      borderColor: brand,
      borderWidth: 1.5,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.78rem",
    color: "#6b7280",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: brand,
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.72rem",
    marginLeft: "4px",
  },
};

const VehicleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [formData, setFormData] = useState({
    vehicleNumber: "",
    chassisNumber: "",
    taxValidity: "",
    fitnessValidity: "",
    insuranceExpiry: "",
    pollutionExpiry: "",
    nationalPermit: "",
    cgPermit: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [existingDocuments, setExistingDocuments] = useState({});
  const [documentsToRemove, setDocumentsToRemove] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Validation states
  const [vehicleNumberError, setVehicleNumberError] = useState("");
  const [chassisNumberError, setChassisNumberError] = useState("");
  const [existingVehicles, setExistingVehicles] = useState([]);

  const documentCategories = [
    {
      label: "RC",
      required: false,
      accept: ".pdf,.jpg,.jpeg,.png",
      fieldName: "rc_document",
    },
    {
      label: "Insurance Policy",
      required: false,
      accept: ".pdf,.jpg,.jpeg,.png",
      fieldName: "insurance_document",
    },
    {
      label: "Pollution/Other Permits",
      required: false,
      accept: ".pdf,.jpg,.jpeg,.png",
      fieldName: "pollution_document",
    },
    {
      label: "Temporary Registration",
      required: false,
      accept: ".pdf,.jpg,.jpeg,.png",
      fieldName: "temp_registration",
    },
    {
      label: "Permit",
      required: false,
      accept: ".pdf,.jpg,.jpeg,.png",
      fieldName: "permit_document",
    },
    {
      label: "Fitness Certificate",
      required: false,
      accept: ".pdf,.jpg,.jpeg,.png",
      fieldName: "fitness_certificate",
    },
  ];

  const fetchExistingVehicles = async () => {
    try {
      const response = await vehicleList("", "", 1);
      let vehicles = [];
      if (response?.data) vehicles = response.data.results || [];
      else if (response?.results) vehicles = response.results || [];
      setExistingVehicles(vehicles);
    } catch (error) {
      console.error("Failed to fetch vehicles:", error);
    }
  };

  useEffect(() => {
    fetchExistingVehicles();
    if (isEditMode && id) fetchVehicleData();
  }, [id, isEditMode]);

  const getVehicleNumberError = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "Vehicle number is required";
    if (!VEHICLE_NUMBER_REGEX.test(trimmed)) {
      return "Enter a valid vehicle number (e.g. CG04 AB 1234)";
    }
    if (isEditMode) {
      const currentVehicle = existingVehicles.find(
        (v) => v.vehicle_id === parseInt(id, 10),
      );
      if (
        currentVehicle &&
        currentVehicle.vehicle_number?.toLowerCase() === trimmed.toLowerCase()
      ) {
        return "";
      }
    }
    const exists = existingVehicles.some(
      (v) => v.vehicle_number?.toLowerCase() === trimmed.toLowerCase(),
    );
    return exists ? "This vehicle number already exists" : "";
  };

  const getChassisNumberError = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "Chassis number is required";
    if (!CHASSIS_NUMBER_REGEX.test(trimmed)) {
      return "Enter a valid chassis number (5-17 letters/numbers, no spaces)";
    }
    if (isEditMode) {
      const currentVehicle = existingVehicles.find(
        (v) => v.vehicle_id === parseInt(id, 10),
      );
      if (
        currentVehicle &&
        currentVehicle.chassis_number?.toLowerCase() === trimmed.toLowerCase()
      ) {
        return "";
      }
    }
    const exists = existingVehicles.some(
      (v) => v.chassis_number?.toLowerCase() === trimmed.toLowerCase(),
    );
    return exists ? "This chassis number already exists" : "";
  };

  const validateVehicleNumber = (value) => {
    const error = getVehicleNumberError(value);
    setVehicleNumberError(error);
    return !error;
  };

  const validateChassisNumber = (value) => {
    const error = getChassisNumberError(value);
    setChassisNumberError(error);
    return !error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "vehicleNumber") validateVehicleNumber(value);
    if (name === "chassisNumber") validateChassisNumber(value);
  };

  // ─── Documents ─────────────────────────────────────────────────
  const handleFileUpload = (label, fieldName, file) => {
    if (!(file instanceof File)) return;

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: `${label} file size should be less than 5MB`,
        severity: "error",
      });
      return;
    }

    setUploadedFiles((prev) => ({ ...prev, [label]: file }));
    setDocumentsToRemove((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });

    setSnackbar({
      open: true,
      message: `${label} uploaded successfully!`,
      severity: "success",
    });
  };

  const handleRemoveFile = (label, fieldName) => {
    const hadNewUpload = !!uploadedFiles[label];
    const hadExisting = !!existingDocuments[label];

    setUploadedFiles((prev) => {
      const next = { ...prev };
      delete next[label];
      return next;
    });

    if (hadNewUpload && hadExisting) {
      setSnackbar({
        open: true,
        message: `${label} reverted to existing document`,
        severity: "info",
      });
      return;
    }

    if (hadExisting) {
      setExistingDocuments((prev) => {
        const next = { ...prev };
        delete next[label];
        return next;
      });
      setDocumentsToRemove((prev) => ({ ...prev, [fieldName]: true }));
    }

    setSnackbar({ open: true, message: `${label} removed`, severity: "info" });
  };

  const handleClearAll = () => {
    setFormData({
      vehicleNumber: "",
      chassisNumber: "",
      taxValidity: "",
      fitnessValidity: "",
      insuranceExpiry: "",
      pollutionExpiry: "",
      nationalPermit: "",
      cgPermit: "",
    });
    setUploadedFiles({});
    setVehicleNumberError("");
    setChassisNumberError("");
    setSnackbar({
      open: true,
      message: "All fields and files cleared!",
      severity: "info",
    });
  };

  const fetchVehicleData = async () => {
    try {
      setPageLoading(true);
      const response = await getVehicleDetails(id);
      if (response) {
        setFormData({
          vehicleNumber: response.vehicle_number || "",
          chassisNumber: response.chassis_number || "",
          taxValidity: response.tax_validity || "",
          fitnessValidity: response.fitness_validity || "",
          insuranceExpiry: response.insurance_expiry || "",
          pollutionExpiry: response.pollution_expiry || "",
          nationalPermit: response.national_permit || "",
          cgPermit: response.cg_permit || "",
        });

        const existingDocs = {};
        documentCategories.forEach((doc) => {
          const possibleFields = [
            `${doc.fieldName}_url`,
            `${doc.fieldName}_link`,
            doc.fieldName,
            `${doc.fieldName.replace("_document", "")}_url`,
            `${doc.fieldName.replace("_document", "")}_link`,
          ];
          let documentUrl = null;
          for (const field of possibleFields) {
            if (response[field]) {
              documentUrl = response[field];
              break;
            }
          }
          if (!documentUrl && response.documents) {
            const docMap = {
              rc_document: "rc",
              insurance_document: "insurance",
              pollution_document: "pollution",
              temp_registration: "temp_registration",
              permit_document: "permit",
              fitness_certificate: "fitness",
            };
            const docKey = docMap[doc.fieldName];
            if (docKey && response.documents[docKey])
              documentUrl = response.documents[docKey];
          }
          if (documentUrl)
            existingDocs[doc.label] = { url: documentUrl, exists: true };
        });
        setExistingDocuments(existingDocs);
      }
    } catch (error) {
      console.error("Error fetching vehicle:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch vehicle data",
        severity: "error",
      });
    } finally {
      setPageLoading(false);
    }
  };

  // ─── MAIN SUBMIT ────────────────────────────────────────────────
  const handleSubmit = async () => {
    const vehicleError = getVehicleNumberError(formData.vehicleNumber);
    const chassisError = getChassisNumberError(formData.chassisNumber);
    setVehicleNumberError(vehicleError);
    setChassisNumberError(chassisError);

    const requiredFields = [
      "vehicleNumber",
      "chassisNumber",
      "taxValidity",
      "fitnessValidity",
      "insuranceExpiry",
    ];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (vehicleError || chassisError) {
      setSnackbar({
        open: true,
        message: vehicleError || chassisError,
        severity: "error",
      });
      return;
    }

    if (missingFields.length > 0) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const formDataObj = new FormData();

      // ── Basic fields ──
      formDataObj.append("vehicle_number", formData.vehicleNumber.trim());
      formDataObj.append("chassis_number", formData.chassisNumber.trim());
      formDataObj.append("tax_validity", formData.taxValidity);
      formDataObj.append("fitness_validity", formData.fitnessValidity);
      formDataObj.append("insurance_expiry", formData.insuranceExpiry);
      if (formData.pollutionExpiry)
        formDataObj.append("pollution_expiry", formData.pollutionExpiry);
      if (formData.nationalPermit)
        formDataObj.append("national_permit", formData.nationalPermit);
      if (formData.cgPermit) formDataObj.append("cg_permit", formData.cgPermit);

      documentCategories.forEach((doc) => {
        const newFile = uploadedFiles[doc.label];
        if (newFile instanceof File) {
          formDataObj.append(doc.fieldName, newFile);
        }
      });

      if (isEditMode) {
        Object.keys(documentsToRemove).forEach((fieldName) => {
          formDataObj.append(`remove_${fieldName}`, "true");
        });
      }

      const vehicleResponse = isEditMode
        ? await updateVehicle(id, formDataObj)
        : await vehicleCreate(formDataObj);

      const payload = vehicleResponse?.data ?? vehicleResponse;
      const explicitFailure =
        payload?.success === false ||
        payload?.error ||
        payload?.status === "error";

      if (explicitFailure) {
        throw new Error(
          payload?.message || payload?.error || "Something went wrong",
        );
      }

      setSnackbar({
        open: true,
        message: `Vehicle ${isEditMode ? "updated" : "created"} successfully!`,
        severity: "success",
      });
      setDocumentsToRemove({});
      setTimeout(() => navigate("/dashboard/VehicleList"), 1200);
    } catch (error) {
      const errData = error?.response?.data;
      let errorMessage = "Something went wrong";

      if (errData) {
        const firstKey = Object.keys(errData)[0];
        const firstMsg = errData[firstKey];
        errorMessage = Array.isArray(firstMsg) ? firstMsg[0] : firstMsg;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setLoading(false);
    }
  };
  const getFileIcon = (file) => {
    if (!file) return <FileIcon sx={{ color: "#6b7280", fontSize: 18 }} />;
    if (file.type === "application/pdf")
      return <PdfIcon sx={{ color: "#dc2626", fontSize: 18 }} />;
    if (file.type.startsWith("image/"))
      return <ImageIcon sx={{ color: brand, fontSize: 18 }} />;
    return <DescriptionIcon sx={{ color: "#666", fontSize: 18 }} />;
  };

  if (pageLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: brand }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1200, mx: "auto", width: "100%" }}
    >
      <Paper
        sx={{
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
          p: 0,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VehicleIcon sx={{ color: brand, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    textAlign: "left",
                  }}
                >
                  {isEditMode ? "Edit Vehicle" : "Add New Vehicle"}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#6b7280", mt: 0.2 }}
                >
                  {isEditMode
                    ? `Editing Vehicle ID: ${id}`
                    : "Fill in the details below to register a vehicle"}
                </Typography>
              </Box>
              {isEditMode && (
                <Chip
                  label={`ID: ${id}`}
                  size="small"
                  sx={{
                    borderRadius: "4px",
                    bgcolor: alpha(brand, 0.1),
                    color: brand,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              )}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/dashboard/VehicleList")}
              size="small"
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2,
                py: 0.75,
                borderColor: "#d1d5db",
                color: "#6b7280",
                boxShadow: "none",
                "&:hover": {
                  borderColor: brand,
                  color: brand,
                  bgcolor: alpha(brand, 0.04),
                  boxShadow: "none",
                },
              }}
            >
              Back to List
            </Button>
          </Box>
        </Box>

        {/* ── Form Content ── */}
        <Box sx={{ p: 2.5 }}>
          {/* Section Label – Vehicle Info */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box
              sx={{ width: 3, height: 18, bgcolor: brand, borderRadius: 4 }}
            />
            <Typography
              sx={{ fontWeight: 700, color: "#111827", fontSize: "0.85rem" }}
            >
              Vehicle Information
            </Typography>
          </Stack>

          {/* ── Vehicle Info Fields ── */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Vehicle Number"
                name="vehicleNumber"
                size="small"
                placeholder="e.g. CG04 AB 1234"
                value={formData.vehicleNumber}
                onChange={handleChange}
                onBlur={(e) => validateVehicleNumber(e.target.value)}
                error={!!vehicleNumberError}
                helperText={vehicleNumberError}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Chassis Number"
                name="chassisNumber"
                size="small"
                placeholder="e.g. CH1234567890"
                value={formData.chassisNumber}
                onChange={handleChange}
                onBlur={(e) => validateChassisNumber(e.target.value)}
                error={!!chassisNumberError}
                helperText={chassisNumberError}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                type="date"
                label="Tax Validity"
                name="taxValidity"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.taxValidity}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                type="date"
                label="Fitness Validity"
                name="fitnessValidity"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.fitnessValidity}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                type="date"
                label="Insurance Expiry"
                name="insuranceExpiry"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.insuranceExpiry}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Pollution Expiry"
                name="pollutionExpiry"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.pollutionExpiry}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="National Permit Expiry"
                name="nationalPermit"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.nationalPermit}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="CG Permit Expiry"
                name="cgPermit"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.cgPermit}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>

          {/* ── Divider ── */}
          <Divider sx={{ my: 3, borderColor: "#e5e7eb" }} />

          {/* Section Label – Documents */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box
              sx={{ width: 3, height: 18, bgcolor: brand, borderRadius: 4 }}
            />
            <Typography
              sx={{ fontWeight: 700, color: "#111827", fontSize: "0.85rem" }}
            >
              Documents
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", ml: 0.5 }}>
              (Max 5MB per file · PDF, JPG, PNG accepted)
            </Typography>
          </Stack>

          {/* ── Document Upload Cards ── */}
          <Grid container spacing={2}>
            {documentCategories.map((doc, index) => {
              const newFile = uploadedFiles[doc.label];
              const existingDoc = existingDocuments[doc.label];
              const isUploaded = !!newFile || !!existingDoc;

              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "4px",
                      border: isUploaded
                        ? "1.5px solid #10b981"
                        : "1.5px dashed #d1d5db",
                      bgcolor: isUploaded ? alpha("#10b981", 0.04) : "#f8fafc",
                      transition: "all 0.2s ease",
                      height: "100%",
                      minHeight: 100,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Card Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          color: "#374151",
                        }}
                      >
                        {doc.label}
                        {doc.required && (
                          <span style={{ color: "#dc2626", marginLeft: 2 }}>
                            *
                          </span>
                        )}
                      </Typography>
                      {isUploaded && (
                        <Chip
                          label={newFile ? "New" : "Saved"}
                          size="small"
                          sx={{
                            borderRadius: "4px",
                            bgcolor: newFile
                              ? alpha(brand, 0.1)
                              : alpha("#10b981", 0.1),
                            color: newFile ? brand : "#10b981",
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            height: 18,
                            px: 0.5,
                          }}
                        />
                      )}
                    </Box>

                    {/* Empty State – Upload Button */}
                    {!isUploaded ? (
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        size="small"
                        startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          borderRadius: "4px",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          py: 0.9,
                          borderColor: "#c7d2fe",
                          color: brand,
                          bgcolor: alpha(brand, 0.04),
                          boxShadow: "none",
                          "&:hover": {
                            borderColor: brand,
                            bgcolor: alpha(brand, 0.09),
                            boxShadow: "none",
                          },
                        }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept={doc.accept}
                          onChange={(e) =>
                            handleFileUpload(
                              doc.label,
                              doc.fieldName,
                              e.target.files[0],
                            )
                          }
                        />
                      </Button>
                    ) : (
                      /* Uploaded State – File name row */
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          bgcolor: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          px: 1,
                          py: 0.75,
                        }}
                      >
                        {/* File icon */}
                        {newFile ? (
                          getFileIcon(newFile)
                        ) : (
                          <FileIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                        )}

                        {/* File name */}
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            fontSize: "0.7rem",
                            color: "#374151",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {newFile
                            ? newFile.name // e.g. license.pdf
                            : existingDoc?.url
                              ? existingDoc.url.split("/").pop() // file name from URL
                              : "Saved document"}
                        </Typography>

                        {/* View button – only for existing server docs */}
                        {!newFile && existingDoc?.url && (
                          <Tooltip title="View document">
                            <IconButton
                              size="small"
                              component={Link}
                              href={existingDoc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: brand,
                                borderRadius: "4px",
                                p: 0.4,
                                "&:hover": { bgcolor: alpha(brand, 0.08) },
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Replace button */}
                        <Tooltip title="Replace file">
                          <IconButton
                            size="small"
                            component="label"
                            sx={{
                              color: "#6b7280",
                              borderRadius: "4px",
                              p: 0.4,
                              "&:hover": {
                                color: brand,
                                bgcolor: alpha(brand, 0.08),
                              },
                            }}
                          >
                            <ReplaceIcon sx={{ fontSize: 15 }} />
                            <input
                              type="file"
                              hidden
                              accept={doc.accept}
                              onChange={(e) =>
                                handleFileUpload(
                                  doc.label,
                                  doc.fieldName,
                                  e.target.files[0],
                                )
                              }
                            />
                          </IconButton>
                        </Tooltip>

                        {/* Remove button */}
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleRemoveFile(doc.label, doc.fieldName)
                            }
                            sx={{
                              color: "#dc2626",
                              borderRadius: "4px",
                              p: 0.4,
                              "&:hover": { bgcolor: alpha("#dc2626", 0.08) },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* ── Action Buttons ── */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={handleClearAll}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: alpha("#dc2626", 0.07),
                color: "#dc2626",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: alpha("#dc2626", 0.14),
                  boxShadow: "none",
                },
              }}
            >
              Clear All
            </Button>

            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SaveIcon sx={{ fontSize: 16 }} />
                )
              }
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: brand,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#4338ca",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  bgcolor: alpha(brand, 0.5),
                  color: "#fff",
                },
              }}
            >
              {loading
                ? "Saving…"
                : isEditMode
                  ? "Update Vehicle"
                  : "Submit Details"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "80px !important",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleForm;
